import { useGLFlags } from '@group-link-one/gl-utils';
import { UTLoading } from '@group-link-one/gl-ui/utilities';
import { AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import type { JSX } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { VerifyRoute } from './VerifyRoute';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { MainLayout } from '@/Layouts';
import { BUManager, Dashboard, Signatures } from '@/Pages';
import { SignatureCreatePage } from '@/Pages/Signatures/SignatureCreatePage';
import { WhatsNew } from '@/Pages/BUs/WhatsNew/WhatsNew';
import { WhatsNewCreatePage } from '@/Pages/BUs/WhatsNew/WhatsNewCreatePage';
import { Pages } from '@/Pages/BUs/Pages/Pages';
import { Tours } from '@/Pages/BUs/Tours/Tours';
import { OptInFeatures } from '@/Pages/BUs/OptInFeatures/OptInFeatures';
import { SurveyBuilderPage } from '@/Pages/BUs/Surveys/SurveyBuilderPage';
import { SurveyResultsPage } from '@/Pages/BUs/Surveys/SurveyResultsPage';
import { Surveys } from '@/Pages/BUs/Surveys/Surveys';
import { OptInFeatureAdoptionPage } from '@/Pages/BUs/OptInFeatures/OptInFeatureAdoptionPage';
import { OptInFeatureFormPage } from '@/Pages/BUs/OptInFeatures/OptInFeatureFormPage';
import { Preferences } from '@/Pages/PlatformUsers/Preferences';
import { UserManagement } from '@/Pages/Settings/UserManagement';
import { UserCreatePage } from '@/Pages/Settings/UserManagement/UserCreatePage';
import { UserOverview } from '@/Pages/Settings/UserOverview';
import { useDashboardSidebarConfig } from '@/config/sidebar.config';
import { firstNavPath } from '@/Shared/components/SidebarNav/SidebarNav';

/**
 * Component that waits for feature flags to load before redirecting to the index route
 */
const IndexRouteRedirect = ({ indexRoute }: { indexRoute: string }): JSX.Element => {
  const { flagsAlreadyLoaded } = useGLFlags();

  if (!flagsAlreadyLoaded) {
    return <UTLoading screen='as-full-screen' type='dots' />;
  }

  return <Navigate to={indexRoute} replace />;
};

export const AuthenticatedRoutes = (): JSX.Element => {
  const location = useLocation();
  const { pages } = usePrivileges();

  /*
    Onde o login cai: a PRIMEIRA linha que a sidebar mostra para esta pessoa.

    Antes eram três flags numa escada, terminando em `/dashboard` de qualquer jeito. Uma flag
    ligada não quer dizer que a pessoa tenha a página: o `research_manager` só enxerga Pesquisas e
    Funcionalidades opt-in, e caía na Visão geral — que ele não pode abrir. O `VerifyRoute` recusa
    e não redireciona, então o resultado era o layout com a área de conteúdo **em branco**, sem
    erro e sem explicação, logo depois de entrar.

    Lido da mesma configuração que desenha a sidebar, e não de uma lista paralela: assim a tela de
    destino não pode discordar do menu. Sem nenhuma linha visível fica `/dashboard`, que é o estado
    honesto de quem não tem página nenhuma.
  */
  const { entries } = useDashboardSidebarConfig(location.pathname);

  const indexRoute = useMemo(() => firstNavPath(entries) ?? '/dashboard', [entries]);

  return (
    <AnimatePresence mode='wait' initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route element={<VerifyRoute needAuth userHasPrivileges element={<MainLayout />} />}>
          {/* Dashboard */}
          <Route
            path='/dashboard'
            element={
              <VerifyRoute
                needAuth
                element={<Dashboard />}
                userHasPrivileges={pages.DASHBOARD.PAGE}
              />
            }
          />

          {/* Business Units */}
          <Route
            path='/business-units'
            element={
              <VerifyRoute
                needAuth
                element={<BUManager />}
                userHasPrivileges={pages.BUSINESS_UNITS.PAGE}
              />
            }
          />

          {/* What's New - Nested under Business Units */}
          <Route
            path='/business-units/whats-new'
            element={
              <VerifyRoute
                needAuth
                element={<WhatsNew />}
                userHasPrivileges={pages.BUSINESS_UNITS.WHATS_NEW.PAGE}
              />
            }
          />

          {/* What's New — creation flow */}
          <Route
            path='/business-units/whats-new/create'
            element={
              <VerifyRoute
                needAuth
                element={<WhatsNewCreatePage />}
                userHasPrivileges={pages.BUSINESS_UNITS.WHATS_NEW.CREATE}
              />
            }
          />

          <Route
            path='/business-units/whats-new/:whatsNewId/edit'
            element={
              <VerifyRoute
                needAuth
                element={<WhatsNewCreatePage />}
                userHasPrivileges={pages.BUSINESS_UNITS.WHATS_NEW.EDIT}
              />
            }
          />

          {/* Pages — Nested under Business Units */}
          <Route
            path='/business-units/pages'
            element={
              <VerifyRoute
                needAuth
                element={<Pages />}
                userHasPrivileges={pages.BUSINESS_UNITS.PAGES.PAGE}
              />
            }
          />

          {/* Tours — Nested under Business Units */}
          <Route
            path='/business-units/tours'
            element={
              <VerifyRoute
                needAuth
                element={<Tours />}
                userHasPrivileges={pages.BUSINESS_UNITS.TOURS.PAGE}
              />
            }
          />

          {/* Opt-in features — beta features a person turns on for themselves */}
          <Route
            path='/opt-in-features'
            element={
              <VerifyRoute
                needAuth
                element={<OptInFeatures />}
                userHasPrivileges={pages.BUSINESS_UNITS.OPT_IN_FEATURES.PAGE}
              />
            }
          />

          {/* Opt-in features — cadastro */}
          <Route
            path='/opt-in-features/new'
            element={
              <VerifyRoute
                needAuth
                element={<OptInFeatureFormPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.OPT_IN_FEATURES.CREATE}
              />
            }
          />

          {/* Opt-in features — adoption. Declared before `/:featureId`, or the literal segment
              would be eaten by the param. */}
          <Route
            path='/opt-in-features/:featureId/adoption'
            element={
              <VerifyRoute
                needAuth
                element={<OptInFeatureAdoptionPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.OPT_IN_FEATURES.PAGE}
              />
            }
          />

          {/* Opt-in features — edição */}
          <Route
            path='/opt-in-features/:featureId'
            element={
              <VerifyRoute
                needAuth
                element={<OptInFeatureFormPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.OPT_IN_FEATURES.EDIT}
              />
            }
          />

          {/* Surveys — questionnaires the dashboards open for people */}
          <Route
            path='/surveys'
            element={
              <VerifyRoute
                needAuth
                element={<Surveys />}
                userHasPrivileges={pages.BUSINESS_UNITS.SURVEYS.PAGE}
              />
            }
          />

          {/* Surveys — builder, creating */}
          <Route
            path='/surveys/new'
            element={
              <VerifyRoute
                needAuth
                element={<SurveyBuilderPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.SURVEYS.CREATE}
              />
            }
          />

          {/* Surveys — results. Declared before `/:surveyId`, or the literal segment would be
              eaten by the param. */}
          <Route
            path='/surveys/:surveyId/results'
            element={
              <VerifyRoute
                needAuth
                element={<SurveyResultsPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.SURVEYS.RESULTS}
              />
            }
          />

          {/* Surveys — builder, editing */}
          <Route
            path='/surveys/:surveyId'
            element={
              <VerifyRoute
                needAuth
                element={<SurveyBuilderPage />}
                userHasPrivileges={pages.BUSINESS_UNITS.SURVEYS.EDIT}
              />
            }
          />

          {/* Signatures */}
          <Route
            path='/signatures'
            element={
              <VerifyRoute
                needAuth
                element={<Signatures />}
                userHasPrivileges={pages.SIGNATURES.PAGE}
              />
            }
          />

          {/* Signatures — creation flow */}
          <Route
            path='/signatures/create'
            element={
              <VerifyRoute
                needAuth
                element={<SignatureCreatePage />}
                userHasPrivileges={pages.SIGNATURES.CREATE}
              />
            }
          />

          {/* Settings - User Management */}
          <Route
            path='/settings/users'
            element={
              <VerifyRoute
                needAuth
                element={<UserManagement />}
                userHasPrivileges={pages.SETTINGS.USERS.PAGE}
              />
            }
          />

          {/* Settings - New user */}
          <Route
            path='/settings/users/create'
            element={
              <VerifyRoute
                needAuth
                element={<UserCreatePage />}
                userHasPrivileges={pages.SETTINGS.USERS.CREATE}
              />
            }
          />

          {/* Settings - User Overview */}
          <Route
            path='/settings/users/:userId'
            element={
              <VerifyRoute
                needAuth
                element={<UserOverview />}
                userHasPrivileges={pages.SETTINGS.USERS.PAGE}
              />
            }
          />

          {/* Platform Users - Preferences */}
          <Route
            path='/platform-users/preferences'
            element={
              <VerifyRoute
                needAuth
                element={<Preferences />}
                userHasPrivileges={pages.PLATFORM_USERS.PREFERENCES.PAGE}
              />
            }
          />

          {/* Root redirect to index route based on features */}
          <Route index path='/' element={<IndexRouteRedirect indexRoute={indexRoute} />} />
        </Route>

        {/* Catch all - redirect to index route */}
        <Route path='*' element={<IndexRouteRedirect indexRoute={indexRoute} />} />
      </Routes>
    </AnimatePresence>
  );
};
